import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Container, FormControlLabel, FormGroup, Grid, Paper, Switch, TextField, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { AuthContext } from '../authContext/AuthContext';
import { setShop } from '../authContext/AuthActions';
import LoaderDialog from './LoaderDialog';

const AppSettings = () => {
  const { user, shop, dispatch } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  
  const [shops, setShops] = useState([]);
  const [items, setItems] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  
  const [selectedShop, setSelectedShop] = useState(shop?.shopkey || '');
  const [selectedMain, setSelectedMain] = useState('');
  const [selectedSub, setSelectedSub] = useState('');
  const [selectedSubSub, setSelectedSubSub] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  
  const [isTokenRequired, setIsTokenRequired] = useState(false);
  const [discountInPercentage, setDiscountInPercentage] = useState(0);
  const [useNativeBrowserPrint, setUseNativeBrowserPrint] = useState(true);
  const [printerName, setPrinterName] = useState("EPSON");
  
  const [shopUsers, setShopUsers] = useState([]);

  const handleUserFileUpload = (index, field, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => handleShopUserChange(index, field, e.target.result);
      reader.readAsText(file);
    }
  };

  const [openSettingsList, setOpenSettingsList] = useState(false);
  const [allSettings, setAllSettings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [openCountersList, setOpenCountersList] = useState(false);
  const [counters, setCounters] = useState([]);
  const [globalFinancialYear, setGlobalFinancialYear] = useState("2627");

  const loadAllSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/settings?manage=true', { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop || shop?.shopkey } });
      setAllSettings(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
      setOpenSettingsList(true);
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Failed to load existing settings.');
    }
  };

  const loadCounters = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/counter', { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop } });
      setCounters(Array.isArray(res.data) ? res.data : []);

      try {
        const fyRes = await axios.get('/settings/find?shopKey=GLOBAL', { headers: { token: 'Bearer ' + user.token } });
        if (fyRes.data && fyRes.data.settings && fyRes.data.settings.financialYear) {
          setGlobalFinancialYear(fyRes.data.settings.financialYear);
        } else {
          setGlobalFinancialYear("2627");
        }
      } catch(e) {
        console.error("Failed to load global financial year", e);
      }

      setLoading(false);
      setOpenCountersList(true);
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Failed to load counters.');
    }
  };

  const handleUpdateGlobalFinancialYear = async () => {
    try {
      setLoading(true);
      const payload = {
        shopKey: "GLOBAL",
        mainCategory: null,
        subCategory: null,
        subSubCategory: null,
        itemKey: null,
        settings: {
          financialYear: globalFinancialYear
        }
      };
      await axios.post('/settings', payload, { headers: { token: 'Bearer ' + user.token, shopkey: "GLOBAL" } });
      setLoading(false);
      alert('Financial Year updated successfully!');
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Failed to update Financial Year.');
    }
  };

  const handleUpdateCounter = async (id, newValue) => {
    try {
      setLoading(true);
      await axios.put(`/counter/set/${id}`, { sequenceValue: Number(newValue) }, { headers: { token: 'Bearer ' + user.token } });
      setLoading(false);
      alert('Counter updated successfully!');
      loadCounters();
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Failed to update counter.');
    }
  };

  const handleCloseSettingsList = () => {
    setOpenSettingsList(false);
  };

  const handleSelectSetting = (setting) => {
    setSelectedShop(setting.shopKey || '');
    setSelectedMain(setting.mainCategory || '');
    setSelectedSub(setting.subCategory || '');
    setSelectedSubSub(setting.subSubCategory || '');
    setSelectedItem(setting.itemKey || '');
    handleCloseSettingsList();
  };

  const handleDeleteSetting = async (id) => {
    if (window.confirm("Are you sure you want to delete this setting?")) {
      try {
        setLoading(true);
        await axios.delete(`/settings/${id}`, { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop } });
        setAllSettings((prev) => prev.filter(setting => setting._id !== id));
        setLoading(false);
        alert('Setting deleted successfully!');
      } catch (err) {
        console.error(err);
        setLoading(false);
        alert('Failed to delete setting.');
      }
    }
  };

  const filteredSettings = allSettings.filter((setting) => {
    const search = searchQuery.toLowerCase();
    return (setting.shopKey || "").toLowerCase().includes(search) ||
           (setting.mainCategory || "").toLowerCase().includes(search) ||
           (setting.subCategory || "").toLowerCase().includes(search) ||
           (setting.subSubCategory || "").toLowerCase().includes(search) ||
           (setting.itemKey && String(setting.itemKey).includes(search));
  });

  useEffect(() => {
    const loadShops = async () => {
      if (!user || !user.token) return;
      try {
        const res = await axios.get('/shops', { headers: { token: 'Bearer ' + user.token } });
        setShops(res.data);
        if (res.data && res.data.length > 0) {
          setSelectedShop((prev) => prev ? prev : (shop?.shopkey || res.data[0].shopkey));
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadShops();
  }, [user, shop?.shopkey]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!user || !user.token) return;
      try {
        const res = await axios.get('/users', { headers: { token: 'Bearer ' + user.token } });
        setAvailableUsers(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    loadUsers();
  }, [user]);

  useEffect(() => {
    const loadItems = async () => {
      if (!selectedShop || !user || !user.token) {
        setItems([]);
        return;
      }
      try {
        const res = await axios.get('/items', { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop } });
        setItems(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    loadItems();
  }, [selectedShop, user]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const loadSettings = async () => {
      if (!selectedShop || !user || !user.token) return;
      try {
        setLoading(true);
        const res = await axios.get(`/settings?manage=true`, { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop }, cancelToken: source.token });
        
        const settingsList = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        const data = settingsList.find(s => 
            (s.mainCategory || '') === selectedMain && 
            (s.subCategory || '') === selectedSub && 
            (s.subSubCategory || '') === selectedSubSub &&
            String(s.itemKey || '') === String(selectedItem || '')
        );

        if (data && data.settings) {
          setIsTokenRequired(data.settings.isTokenRequired || false);
          setDiscountInPercentage(data.settings.discountInPercentage || 0);
          setUseNativeBrowserPrint(data.settings.useNativeBrowserPrint ?? true);
          setPrinterName(data.settings.printerName || "EPSON");
          setShopUsers(data.settings.usersWithCertificates || []);
        } else {
          setIsTokenRequired(false);
          setDiscountInPercentage(0);
          setUseNativeBrowserPrint(true);
          setPrinterName("EPSON");
          setShopUsers([]);
        }
        setLoading(false);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error(err);
        setIsTokenRequired(false);
        setDiscountInPercentage(0);
        setUseNativeBrowserPrint(true);
        setPrinterName("EPSON");
        setShopUsers([]);
        setLoading(false);
      }
    };
    loadSettings();
    return () => {
      source.cancel();
    };
  }, [selectedShop, selectedMain, selectedSub, selectedSubSub, selectedItem, user]);

  const handleAddUserCertificate = () => {
    setShopUsers([...shopUsers, { userId: "", qzPrivateKey: "", qzCertificate: "", printerName: "" }]);
  };

  const handleRemoveUserCertificate = (index) => {
    const updatedList = [...shopUsers];
    updatedList.splice(index, 1);
    setShopUsers(updatedList);
  };

  const handleShopUserChange = (index, field, value) => {
    const updatedList = [...shopUsers];
    updatedList[index][field] = value;
    setShopUsers(updatedList);
  };

  const handleSave = async () => {
    if (!user || !user.token) {
      alert("User authentication error. Please login again.");
      return;
    }
    try {
      const userIds = shopUsers.map(u => u.userId).filter(Boolean);
      if (new Set(userIds).size !== userIds.length) {
        alert("Each user must be unique. Please remove duplicate users.");
        setLoading(false);
        return;
      }

      const printerNames = shopUsers.map(u => u.printerName).filter(Boolean);
      if (new Set(printerNames).size !== printerNames.length) {
        alert("Each printer name must be unique. Please remove duplicate printer names.");
        setLoading(false);
        return;
      }

      setLoading(true);
      const isShopLevel = !selectedMain && !selectedSub && !selectedSubSub && !selectedItem;
      const payload = {
        shopKey: selectedShop,
        mainCategory: selectedMain || null,
        subCategory: selectedSub || null,
        subSubCategory: selectedSubSub || null,
        itemKey: selectedItem ? Number(selectedItem) : null,
        settings: {
          isTokenRequired,
          discountInPercentage: Number(discountInPercentage),
          ...(isShopLevel && {
            useNativeBrowserPrint,
            printerName,
            usersWithCertificates: shopUsers
          })
        }
      };
      await axios.post('/settings', payload, { headers: { token: 'Bearer ' + user.token, shopkey: selectedShop } });
      
      if (selectedShop === shop?.shopkey) {
        try {
          const settingsRes = await axios.get("/settings", {headers: {token:"Bearer " + user.token, shopkey: shop.shopkey}});
          const updatedSettings = Array.isArray(settingsRes.data) ? settingsRes.data : [];
          dispatch(setShop({ ...shop, settings: updatedSettings }, user));
        } catch(e) {
          console.log("Failed to update context", e);
        }
      }

      setLoading(false);
      alert('Settings saved successfully!');
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Failed to save settings.');
    }
  };

  const mainCategories = [...new Set((items || []).map(item => item.itemMainCategory).filter(Boolean))];
  const subCategories = [...new Set((items || []).filter(item => !selectedMain || item.itemMainCategory === selectedMain).map(item => item.itemSubCategory).filter(Boolean))];
  const subSubCategories = [...new Set((items || []).filter(item => (!selectedMain || item.itemMainCategory === selectedMain) && (!selectedSub || item.itemSubCategory === selectedSub)).map(item => item.itemSubsubCategory).filter(Boolean))];

  const itemsForDropdown = (items || []).filter(item => {
    if (!selectedMain) return false;
    if (item.itemMainCategory !== selectedMain) return false;
    if (selectedSub && item.itemSubCategory !== selectedSub) return false;
    if (selectedSubSub && item.itemSubsubCategory !== selectedSubSub) return false;
    return true;
  });

  return (
    <Container maxWidth="false" disableGutters sx={{ height: '92vh', width: '100vw', p: 2 }}>
      <LoaderDialog open={loading} title="Processing Settings..." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" color="primary">Admin Settings</Typography>
        <Box>
          <Button variant="outlined" onClick={loadCounters} sx={{ mr: 2 }}>Manage Counters</Button>
          <Button variant="outlined" onClick={loadAllSettings}>View Existing Settings</Button>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{ p: 2, height: '80vh', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Hierarchy Level</Typography>
            <Divider sx={{ mb: 2 }} />

            <TextField select label="Shop" value={selectedShop} onChange={(e) => { setSelectedShop(e.target.value); setSelectedMain(''); setSelectedSub(''); setSelectedSubSub(''); setSelectedItem(''); }} variant="standard" SelectProps={{ native: true }} fullWidth sx={{ mb: 3 }}>
              <option value=""></option>
              {shops.map(shop => (<option key={shop.shopkey} value={shop.shopkey}>{shop.shopname} ({shop.shopkey})</option>))}
            </TextField>
            <TextField select label="Main Category" value={selectedMain} onChange={(e) => { setSelectedMain(e.target.value); setSelectedSub(''); setSelectedSubSub(''); setSelectedItem(''); }} variant="standard" SelectProps={{ native: true }} fullWidth sx={{ mb: 3 }} disabled={!selectedShop}>
              <option value=""></option>
              {mainCategories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
            </TextField>
            <TextField select label="Sub Category" value={selectedSub} onChange={(e) => { setSelectedSub(e.target.value); setSelectedSubSub(''); setSelectedItem(''); }} variant="standard" SelectProps={{ native: true }} fullWidth sx={{ mb: 3 }} disabled={!selectedMain}>
              <option value=""></option>
              {subCategories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
            </TextField>
            <TextField select label="Sub Sub Category" value={selectedSubSub} onChange={(e) => { setSelectedSubSub(e.target.value); setSelectedItem(''); }} variant="standard" SelectProps={{ native: true }} fullWidth sx={{ mb: 3 }} disabled={!selectedSub}>
              <option value=""></option>
              {subSubCategories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
            </TextField>
            <TextField select label="Item" value={selectedItem} onChange={(e) => setSelectedItem(e.target.value)} variant="standard" SelectProps={{ native: true }} fullWidth sx={{ mb: 3 }} disabled={!selectedMain}>
              <option value=""></option>
              {itemsForDropdown.map(item => (<option key={item.itemKey} value={item.itemKey}>{item.itemName} ({item.itemKey})</option>))}
            </TextField>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <Paper sx={{ p: 2, height: '80vh', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Properties Configuration</Typography>
            <Divider sx={{ mb: 2 }} />
            {selectedShop ? (
              <Box>
                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  Settings target: <strong>{selectedShop}</strong> {selectedMain && `> ${selectedMain}`} {selectedSub && `> ${selectedSub}`} {selectedSubSub && `> ${selectedSubSub}`} {selectedItem && `> Item: ${selectedItem}`}
                </Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  {!selectedMain && !selectedSub && !selectedSubSub && !selectedItem && (
                    <>
                      <Grid item xs={12}>
                        <FormGroup>
                          <FormControlLabel control={<Switch checked={useNativeBrowserPrint} onChange={(e) => setUseNativeBrowserPrint(e.target.checked)} />} label="Use Native Browser Print (Disable for Direct Thermal ESC/POS)" />
                        </FormGroup>
                      </Grid>
                      
                      {!useNativeBrowserPrint && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>Shop Users & QZ Certificates</Typography>
                          </Grid>
                          {shopUsers.map((item, index) => {
                            const selectedUserIds = shopUsers.filter((u, i) => i !== index).map(u => u.userId);
                            const usersForDropdown = availableUsers.filter(u => !selectedUserIds.includes(u._id));
                            return (
                              <Grid item xs={12} key={index}>
                                <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
                                  <IconButton 
                                    color="error" 
                                    onClick={() => handleRemoveUserCertificate(index)}
                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                      <FormControl fullWidth variant="standard">
                                        <InputLabel>Select User</InputLabel>
                                        <Select
                                          value={usersForDropdown.some(u => u._id === item.userId) ? item.userId : ""}
                                          onChange={(e) => handleShopUserChange(index, 'userId', e.target.value)}
                                        >
                                          {usersForDropdown.map((u) => (
                                            <MenuItem key={u._id} value={u._id}>{u.username}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        label="Printer Name"
                                        variant="standard"
                                        fullWidth
                                        value={item.printerName || ""}
                                        onChange={(e) => handleShopUserChange(index, 'printerName', e.target.value)}
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Button variant="contained" component="label" fullWidth sx={{ mb: 1 }} size="small">
                                            Upload Private Key (.pem)
                                            <input type="file" hidden accept=".pem,.txt" onChange={(e) => handleUserFileUpload(index, 'qzPrivateKey', e)} />
                                          </Button>
                                          <TextField
                                            label="Private Key Content"
                                            multiline
                                            rows={3}
                                            fullWidth
                                            variant="outlined"
                                            value={item.qzPrivateKey || ""}
                                            onChange={(e) => handleShopUserChange(index, 'qzPrivateKey', e.target.value)}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Button variant="contained" component="label" fullWidth sx={{ mb: 1 }} size="small">
                                            Upload Certificate (.txt)
                                            <input type="file" hidden accept=".crt,.txt,.pem" onChange={(e) => handleUserFileUpload(index, 'qzCertificate', e)} />
                                          </Button>
                                          <TextField
                                            label="Certificate Content"
                                            multiline
                                            rows={3}
                                            fullWidth
                                            variant="outlined"
                                            value={item.qzCertificate || ""}
                                            onChange={(e) => handleShopUserChange(index, 'qzCertificate', e.target.value)}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </Grid>
                            );
                          })}
                          <Grid item xs={12}>
                            <Button variant="outlined" onClick={handleAddUserCertificate} fullWidth>
                              + Add User & Certificates
                            </Button>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel control={<Switch checked={isTokenRequired} onChange={(e) => setIsTokenRequired(e.target.checked)} />} label="Is Token Required" />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Discount In Percentage (%)" type="number" variant="standard" fullWidth value={discountInPercentage} onChange={(e) => setDiscountInPercentage(e.target.value)} inputProps={{ min: 0, max: 100 }} />
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} size="large">Save Settings</Button>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Typography color="textSecondary">Please select a Shop from the left menu to start configuring properties.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={openSettingsList} onClose={handleCloseSettingsList} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Existing Settings
          <TextField 
            size="small" 
            placeholder="Search..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Shop</TableCell>
                  <TableCell>Main Category</TableCell>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>Sub Sub Category</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Token Req.</TableCell>
                  <TableCell>Discount (%)</TableCell>
                  <TableCell>Native Print</TableCell>
                  <TableCell>Printer Name</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSettings.map((setting, idx) => (
                  <TableRow key={setting._id || idx}>
                    <TableCell>{setting.shopKey}</TableCell>
                    <TableCell>{setting.mainCategory || '-'}</TableCell>
                    <TableCell>{setting.subCategory || '-'}</TableCell>
                    <TableCell>{setting.subSubCategory || '-'}</TableCell>
                    <TableCell>{setting.itemKey || '-'}</TableCell>
                    <TableCell>{setting.settings?.isTokenRequired ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{setting.settings?.discountInPercentage || 0}</TableCell>
                    <TableCell>{(setting.mainCategory || setting.subCategory || setting.subSubCategory || setting.itemKey) ? '-' : (setting.settings?.useNativeBrowserPrint ? 'Yes' : 'No')}</TableCell>
                    <TableCell>{(setting.mainCategory || setting.subCategory || setting.subSubCategory || setting.itemKey) ? '-' : (setting.settings?.printerName || '-')}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleSelectSetting(setting)} color="primary" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteSetting(setting._id)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSettings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">No settings found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettingsList}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openCountersList} onClose={() => setOpenCountersList(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Counters & App Settings</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>Global Application Settings</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <TextField 
                  label="Financial Year (e.g. 2627)" 
                  size="small" 
                  fullWidth 
                  value={globalFinancialYear} 
                  onChange={(e) => setGlobalFinancialYear(e.target.value)} 
                />
              </Grid>
              <Grid item xs={4}>
                <Button variant="contained" color="primary" onClick={handleUpdateGlobalFinancialYear} fullWidth>
                  Save Year
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>Invoice Counters</Typography>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Counter ID</TableCell>
                  <TableCell>Sequence Value</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counters.map((c) => (
                  <TableRow key={c._id}>
                    <TableCell>{c._id}</TableCell>
                    <TableCell>
                      <TextField 
                        size="small" 
                        type="number"
                        defaultValue={c.sequenceValue || 0} 
                        id={`counter-input-${c._id}`}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={() => {
                          const val = document.getElementById(`counter-input-${c._id}`).value;
                          handleUpdateCounter(c._id, val);
                        }}
                      >
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {counters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No counters found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCountersList(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppSettings;